import { useNavigate } from 'react-router-dom';
import { radioStations } from '@/mocks/radios';
import type { RadioEvent } from '@/mocks/adminEvents';
import {
  eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels,
  eventModalityLabels, eventModalityIcons,
  eventCategoryLabels, eventCategoryIcons,
} from '@/mocks/adminEvents';

interface EventCardProps {
  event: RadioEvent;
  onClick?: (event: RadioEvent) => void;
  onShare?: (event: RadioEvent) => void;
  onSave?: (event: RadioEvent) => void;
  isSaved?: boolean;
}

export default function EventCard({ event, onClick, onShare, onSave, isSaved = false }: EventCardProps) {
  const navigate = useNavigate();
  const station = radioStations.find((s) => s.id === event.stationId);
  const isToday = event.status === 'publicado';
  const isPast = event.status === 'finalizado';
  const isCancelled = event.status === 'cancelado';
  const hasCapacity = event.capacity > 0;
  const typeColors = eventTypeColors[event.type];
  const modalityLabel = eventModalityLabels[event.modality];
  const modalityIcon = eventModalityIcons[event.modality];

  const modalityStyle = event.modality === 'presencial' ? 'bg-amber-50 text-amber-700' : event.modality === 'online' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700';

  const handleStationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (station) {
      navigate(`/radio/${station.frequency.replace('.', '-')}`);
    }
  };

  const handleCardClick = () => {
    onClick?.(event);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(event);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSave?.(event);
  };

  const dateObj = new Date(event.date);
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString('es-MX', { month: 'short' });

  return (
    <div
      onClick={handleCardClick}
      className={`relative bg-background-50 rounded-2xl border overflow-hidden transition-all group cursor-pointer ${
        isPast ? 'border-background-200/70 opacity-80' :
        isCancelled ? 'border-red-200/70 opacity-70' :
        'border-background-200 hover:border-foreground-300'
      }`}
    >
      {/* Image */}
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={event.image}
          alt={event.title}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isPast ? 'grayscale-[30%]' : ''}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

        {/* Date badge */}
        <div className={`absolute top-3 left-3 w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 ${
          isToday ? 'bg-red-500 text-background-50' :
          isCancelled ? 'bg-red-500 text-background-50' :
          isPast ? 'bg-foreground-400 text-background-50' :
          'bg-background-50 text-foreground-950'
        }`}>
          <span className="text-xs font-bold uppercase leading-none">{month}</span>
          <span className="text-lg font-extrabold leading-none">{day}</span>
        </div>

        {/* Status badge */}
        <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold ${
          isToday ? 'bg-accent-500 text-background-50' :
          isCancelled ? 'bg-red-500 text-background-50' :
          isPast ? 'bg-foreground-300 text-foreground-700' :
          'bg-secondary-500 text-background-50'
        }`}>
          {eventStatusLabels[event.status]}
        </div>

        {event.highlighted && (
          <div className="absolute top-14 right-3 px-2 py-0.5 rounded-full bg-amber-500 text-background-50 text-[9px] font-bold">
            Destacado
          </div>
        )}

        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100">
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {onShare && (
              <button onClick={handleShare} className="w-8 h-8 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer" title="Compartir">
                <i className="ri-share-forward-line text-foreground-700 text-xs"></i>
              </button>
            )}
            {onSave && (
              <button onClick={handleSave} className={`w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors cursor-pointer ${isSaved ? 'bg-secondary-500 text-background-50' : 'bg-background-50/90 hover:bg-background-50 text-foreground-700'}`} title={isSaved ? 'Guardado' : 'Guardar'}>
                <i className={`${isSaved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-xs`}></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Category + Type + Modality */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <i className={`${eventCategoryIcons[event.category]} text-[11px] text-accent-600`}></i>
          <span className="text-[10px] font-bold text-accent-600 uppercase tracking-wider">{eventCategoryLabels[event.category]}</span>
          <span className="text-[10px] text-foreground-400">·</span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
            <i className={`${eventTypeIcons[event.type]} text-[8px]`}></i>
            {eventTypeLabels[event.type]}
          </span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${modalityStyle}`}>
            <i className={`${modalityIcon} text-[8px]`}></i>
            {modalityLabel}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-sm font-bold text-foreground-950 leading-snug line-clamp-2">{event.title}</h3>

        {/* Description */}
        <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2">{event.description}</p>

        {/* Info rows */}
        <div className="space-y-1.5 pt-1">
          <div className="flex items-center gap-2 text-[11px] text-foreground-600">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-time-line text-foreground-400 text-xs"></i>
            </div>
            <span className="font-medium">{event.time}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-foreground-600">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-foreground-400 text-xs"></i>
            </div>
            <span className="font-medium truncate">{event.location}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-foreground-600">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-group-line text-foreground-400 text-xs"></i>
            </div>
            <span className="font-medium">{event.participants.toLocaleString()} participantes · {event.interested.toLocaleString()} interesados</span>
          </div>
          {hasCapacity && (
            <div className="flex items-center gap-2 text-[11px] text-foreground-600">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-line text-foreground-400 text-xs"></i>
              </div>
              <span className="font-medium">Cupo: {event.capacity.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Station organizer */}
        {station && (
          <div className="flex items-center gap-2 pt-2 border-t border-background-100">
            <div className="w-8 h-8 rounded-lg bg-background-200 overflow-hidden shrink-0">
              <img src={station.logo} alt={station.name} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-foreground-400">Organiza</p>
              <button
                onClick={handleStationClick}
                className="text-[11px] font-semibold text-foreground-700 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {station.name}
              </button>
            </div>
            <span className="text-[10px] text-foreground-400 bg-background-100 rounded-md px-1.5 py-0.5">{station.frequency} RFW</span>
          </div>
        )}
      </div>
    </div>
  );
}